import React from 'react'
import { getDynamicStyles } from 'jss'
import { createGenerateId } from '@airbus/elements/css-from-js'
import { StylesContext } from './StylesProvider'
import { useColorMode } from './ColorModeProvider'

export type StyleFunc = (theme: any) => any

let counter = 0
const createStylesheet = (styles: any, context: any, name: string, isDynamic = false, isDark = false) => {
  let nameWithDark = name
  if (isDark && process.env.NODE_ENV !== 'test') nameWithDark = name + '-dark'
  const sheet = context.jss.createStyleSheet(styles, {
    link: isDynamic,
    generateId:
      process.env.NODE_ENV === 'test' || process.env.AIRBUS_JSS === 'SSR'
        ? createGenerateId('' + nameWithDark)
        : createGenerateId('' + nameWithDark + counter++),
  })
  context.registry.add(sheet)
  return sheet
}

/**
 * One sheet per element/style function
 */
type Sheet = {
  /** the classes generated by JSS */
  classes: any
  /** the associated JSS stylesheet */
  sheet: any
  /** styles dependent on props values */
  dynamicStyles: any
}

type SheetCache = Map<StyleFunc, Sheet>
const cache = new Map<any, SheetCache>()

/**
 * Returns the Sheet corresponding to a style function, retrieving it from the cache or
 * generating it if it doesn't exist yet
 */
const getCachedSheet = (styleFn: StyleFunc, theme: any, context: any, name: string): Sheet => {
  let themeCache = cache.get(theme)
  if (themeCache) {
    const cachedSheet = themeCache.get(styleFn) as any
    if (cachedSheet) {
      return cachedSheet
    }
  } else {
    themeCache = new Map() as SheetCache
    cache.set(theme, themeCache)
  }
  const styles = styleFn(theme)
  const dynamicStyles = getDynamicStyles(styles)

  const sheet = createStylesheet(styles, context, name, false, theme.color.type === 'dark')
  const createdSheet = {
    classes: sheet.classes,
    sheet,
    dynamicStyles,
  }
  themeCache.set(styleFn, createdSheet)
  return createdSheet
}

const mergeClasses = (staticClasses: any, dynamicClasses: any) => {
  if (!dynamicClasses) {
    return staticClasses
  }
  const retClasses = { ...staticClasses }

  Object.keys(dynamicClasses).forEach((key) => {
    if (staticClasses[key]) {
      retClasses[key] = `${staticClasses[key]} ${dynamicClasses[key]}`
    }
  })
  return retClasses
}

export default (styleFn: StyleFunc, name: string) => {
  return function useStyles(props: any = {}) {
    const context = React.useContext(StylesContext)
    const theme = useColorMode()

    const [staticSheet, dynamicSheet, classes] = React.useMemo(() => {
      const cachedSheet = getCachedSheet(styleFn, theme, context, name)

      const dynamicSheet = cachedSheet.dynamicStyles
        ? createStylesheet(cachedSheet.dynamicStyles, context, name, true, theme.color.type === 'dark')
        : null

      const classes = dynamicSheet ? mergeClasses(cachedSheet.classes, dynamicSheet.classes) : cachedSheet.classes

      return [cachedSheet.sheet, dynamicSheet, classes]
    }, [context, theme])

    React.useLayoutEffect(() => {
      if (!staticSheet.attached) {
        staticSheet.attach()
      }
      if (dynamicSheet) {
        dynamicSheet.update(props)
        if (!dynamicSheet.attached) {
          dynamicSheet.attach()
        }
      }
      return () => {
        if (dynamicSheet) {
          context.jss.removeStyleSheet(dynamicSheet)
        }
      }
    }, [context, staticSheet, dynamicSheet, props])

    return classes
  }
}
